import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

/**
 * Lazy-load the event registry in development for runtime validation.
 * Returns null in production (no validation needed).
 */
let registryPromise = null;
function getRegistry() {
    if (import.meta.env.PROD) return null;
    if (!registryPromise) {
        registryPromise = import('../../../../analytics/events.json')
            .then(m => m.default || m)
            .catch(() => null);
    }
    return registryPromise;
}

/**
 * Initialize PostHog on every page.
 * Call once from main.jsx (which runs on all layouts).
 */
export function initPostHog() {
    if (initialized || !POSTHOG_KEY) return;
    initialized = true;

    const isSignupPage = /^\/(sign-up|uabmedicine|wellthy|vivvi)/.test(window.location.pathname);

    posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: 'identified_only',
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: !isSignupPage,
        disable_session_recording: true,
        persistence: 'localStorage+cookie',
        cross_subdomain_cookie: true,
        cookie_domain: '.wyndy.com',
    });

    posthog.register({ platform: 'web' });
}

/**
 * Identify a parent after signup or returning sign-in.
 * Uses the same distinct ID format as the mobile app: 'parent-<id>'
 */
export function identifyParent(parentId) {
    if (!parentId || !initialized) return;
    posthog.identify(`parent-${parentId}`, { userType: 'parent' });
}

/**
 * Reset identity on logout so the next visitor isn't merged.
 */
export function resetIdentity() {
    if (!initialized) return;
    posthog.reset();
}

/**
 * Track a named event with properties.
 * In development, warns on unregistered events or missing required properties.
 */
export function trackEvent(eventName, properties = {}) {
    if (!initialized) return;

    if (import.meta.env.DEV) {
        const registry = getRegistry();
        if (registry) {
            registry.then(data => {
                if (!data?.events) return;
                const def = data.events[eventName];
                if (!def) {
                    console.warn(`[PostHog] Unregistered event: "${eventName}"`);
                    return;
                }
                if (def.properties) {
                    for (const [prop, propDef] of Object.entries(def.properties)) {
                        if (propDef.required && !(prop in properties)) {
                            console.warn(`[PostHog] Missing required property "${prop}" for event "${eventName}"`);
                        }
                    }
                }
            });
        }
    }

    posthog.capture(eventName, properties);
}

/**
 * Track a link click event, then navigate.
 * Matches the old window.trackLinkEvent signature.
 */
export function trackLinkEvent(link, eventName, properties = {}) {
    trackEvent(eventName, properties);
    if (link?.href) {
        window.location.href = link.href;
    }
    return false;
}

export { posthog };